import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { retrieveWorksheetAPI, createAssignmentAPI } from "./worksheetAPI";
import { getRandom5Sentences } from "../../utils/worksheetUtils";

const initialState = {
  worksheet: { title: "", data: [] }, // 단일 Worksheet 데이터
  loading: false, // 로딩 상태
  error: null, // 에러 상태
};

export const retrieveWorksheet = createAsyncThunk(
  "worksheet/retrieveWorksheet",
  async ({ id, type, author_name }, { rejectWithValue }) => {
    try {
      const response = await retrieveWorksheetAPI(id, type);

      if (type === "analysis") {
        const title = response.title;
        const analysisDocs = response.data.documents.filter(
          (doc) => doc.type === "analysis"
        )[0];
        const examDocs = response.data.online_documents.filter(
          (doc) => doc.type === "analysis:exam"
        )[0];

        const processedParagraphs = [];

        for (let idx = 0; idx < analysisDocs.paragraph_data.length; idx++) {
          const para = analysisDocs.paragraph_data[idx];
          const exam = examDocs.paragraph_data[idx].exams;
          const sectionDataList = [];
          const keypoints = para.keypoints || null;
          const words = para.words || [];
          const syntax_analysis = para.syntax_analysis || [];
          const sentences = para.sentence_eng_kor || [];

          const korBlank = exam
            .filter((item) => item.type === "analysis:exam:kor_blank")
            .map((item) => item.data);
          const engBlank = exam
            .filter((item) => item.type === "analysis:exam:eng_blank")
            .map((item) => item.data);
          const engBlankRandom = exam
            .filter((item) => item.type === "analysis:exam:eng_blank_random")
            .map((item) => item.data);
          const engScramble = exam
            .filter((item) => item.type === "analysis:exam:eng_scramble")
            .map((item) => item.data);

          sectionDataList.push({
            type: "keypoints",
            data: keypoints,
          });
          sectionDataList.push({
            type: "reading",
            data: sentences,
          });
          sectionDataList.push({
            type: "words",
            data: words,
          });
          sectionDataList.push({
            type: "syntax_analysis",
            data: syntax_analysis,
          });
          sectionDataList.push({
            type: "flow",
            data: getRandom5Sentences(sentences),
          });
          sectionDataList.push({
            type: "sentence_test",
            data: [
              { type: "kor_blank", data: korBlank },
              { type: "eng_blank", data: engBlank },
              { type: "eng_blank_random", data: engBlankRandom },
              { type: "eng_scramble", data: engScramble },
            ],
          });

          const newItem = {
            title: sentences[0][0].split(" ").slice(0, 6).join(" ") + "...",
            data: sectionDataList,
          };

          processedParagraphs.push(newItem);
        }

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: processedParagraphs,
        };
      } else if (type === "comprehensive") {
        const title = response.title;
        const comprehensiveDocs = response.data.documents.filter(
          (doc) => doc.type === "comprehensive"
        );

        const processedSetList = [];

        for (let idx = 0; idx < comprehensiveDocs.length; idx++) {
          const comprehensiveDoc = comprehensiveDocs[idx];
          const processedParagraphs = [];

          for (
            let idx = 0;
            idx < comprehensiveDoc.paragraph_data.length;
            idx++
          ) {
            const para = comprehensiveDoc.paragraph_data[idx];
            const full_text = para.paragraph || "";
            const full_text_korean = para.full_text_korean || "";
            const elements = para.elements || [];

            const processedData = [];

            for (let elementType of [
              "scramble",
              "understanding",
              "keyword",
              "translation",
              "ox",
              "summary",
            ]) {
              let elementData = elements.filter((element) =>
                element.type.includes(elementType)
              );
              if (["ox", "summary"].includes(elementType)) {
                elementData = elementData.flatMap((selectedElement) =>
                  selectedElement.problems.map((problem) => {
                    return {
                      ...problem,
                      type: selectedElement.type.split(":")[1],
                    };
                  })
                );
              } else {
                elementData = elementData.flatMap(
                  (element) => element.problems
                );
              }

              if (elementData.length > 0) {
                processedData.push({
                  type: elementType,
                  data: elementData,
                });
              }
            }
            processedParagraphs.push({
              title: full_text.split(" ").slice(0, 6).join(" ") + "...",
              full_text,
              full_text_korean,
              data: processedData,
            });
          }
          processedSetList.push(processedParagraphs);
        }

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: processedSetList,
        };
      } else if (type === "paragraph") {
        const title = response.title;
        const paragraphDocs = response.data.documents;

        const processedData = [];

        for (let idx = 0; idx < paragraphDocs.length; idx++) {
          const sectionData = paragraphDocs[idx];
          if (sectionData.subtitle.includes("요약문")) {
            continue;
          }
          processedData.push({
            type_name: sectionData.subtitle,
            data: sectionData.data
              .filter((element) => element.type != "paragraph:summary_blank")
              .map((selectedElement) => ({
                ...selectedElement,
                type: selectedElement.type.split(":")[1],
              })),
          });
        }

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: [{ title: "모의고사", data: processedData }],
        };
      } else if (type === "vocabulary") {
        const title = response.title;
        const wordList = response.data.word_list;
        const examList = response.data.exams;

        const newParagraphList = [];

        const studyDataList = [];
        wordList.forEach((_, index) => {
          if (index % 100 === 0) {
            const start = index + 1;
            const end = Math.min(index + 100, wordList.length);
            const typeName = `단어장 ${String(start).padStart(3, "0")}-${String(
              end
            ).padStart(3, "0")}`;

            studyDataList.push({
              type: "study",
              type_name: typeName,
              data: { words: wordList.slice(index, index + 100) },
            });
          }
        });
        newParagraphList.push({
          title: "[단어장]",
          data: studyDataList,
        });

        const matchingDataList = [];
        wordList.forEach((_, index) => {
          if (index % 100 === 0) {
            const start = index + 1;
            const end = Math.min(index + 100, wordList.length);
            const typeName = `매칭게임 ${String(start).padStart(
              3,
              "0"
            )}-${String(end).padStart(3, "0")}`;

            matchingDataList.push({
              type: "matching",
              type_name: typeName,
              data: { words: wordList.slice(index, index + 100) },
            });
          }
        });
        newParagraphList.push({
          title: "[매칭게임]",
          data: matchingDataList,
        });

        const testDataList = [];
        examList.forEach((examData, examIndex) => {
          const newExamData = [];
          const typeName = "테스트 " + (examIndex + 1);
          const problems = examData.problems;

          problems.engkor_choice.forEach((problem) => {
            newExamData.push({
              type: "engkor_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].word,
              question: "주어진 영단어의 한국어 뜻을 고르시오.",
              choices: problem.choices_word_indices.map((wordIndex, index) =>
                wordList[wordIndex].kor
                  .split("\n")
                  [problem.choice_line_indices[index]].replace(/\[.*?\]/g, "")
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.koreng_choice.forEach((problem) => {
            newExamData.push({
              type: "koreng_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].kor
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 한국어 뜻에 해당하는 영단어를 고르시오.",
              choices: problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.syn_choice.forEach((problem) => {
            newExamData.push({
              type: "syn_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].syn
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 동의어를 가진 영단어를 고르시오.",
              choices: problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.ant_choice.forEach((problem) => {
            newExamData.push({
              type: "ant_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].ant
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 반의어를 가진 영단어를 고르시오.",
              choices: problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.engeng_choice.forEach((problem) => {
            newExamData.push({
              type: "engeng_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].eng
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 영영 뜻풀이를 가진 영단어를 고르시오.",
              choices: problem.choices_word_indices.map(
                (idx) => wordList[idx].word
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.example_blank_choice.forEach((problem) => {
            newExamData.push({
              type: "example_blank_choice",
              answer: problem.answer_choice_idx + 1,
              text: wordList[problem.answer_word_idx].example
                .split("\n")
                [problem.question_line_idx].replace(/\{.*?\}/g, (match) =>
                  "_".repeat(match.length)
                ),
              text1:
                wordList[problem.answer_word_idx].example_translation.split(
                  "\n"
                )[problem.question_line_idx],
              question:
                "주어진 한국어 뜻을 참고하여 예문의 빈칸에 들어갈 가장 알맞은 영단어를 고르시오.",
              choices: problem.choices_word_indices.map(
                (wordIdx, index) =>
                  wordList[wordIdx].example
                    .split("\n")
                    [problem.choice_line_indices[index]].match(/\{(.*?)\}/)[1]
              ),
              word: wordList[problem.answer_word_idx].word,
            });
          });

          problems.engkor.forEach((problem) => {
            newExamData.push({
              type: "engkor",
              answer: wordList[problem.answer_word_idx].kor.replace("\n", " "),
              text: wordList[problem.answer_word_idx].word,
              question: "주어진 영단어의 한국어 뜻을 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.koreng.forEach((problem) => {
            newExamData.push({
              type: "koreng",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].kor
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 한국어 뜻에 해당하는 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.syn.forEach((problem) => {
            newExamData.push({
              type: "syn",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].syn
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 동의어를 가진 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.ant.forEach((problem) => {
            newExamData.push({
              type: "ant",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].ant
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 반의어를 가진 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.engeng.forEach((problem) => {
            newExamData.push({
              type: "engeng",
              answer: wordList[problem.answer_word_idx].word,
              text: wordList[problem.answer_word_idx].eng
                .split("\n")
                [problem.question_line_idx].replace(/\[.*?\]/g, ""),
              question: "주어진 영영 뜻풀이를 가진 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          problems.example_blank.forEach((problem) => {
            newExamData.push({
              type: "example_blank",
              answer: wordList[problem.answer_word_idx].example
                .split("\n")
                [problem.question_line_idx].match(/\{(.*?)\}/)[1],
              text: wordList[problem.answer_word_idx].example
                .split("\n")
                [problem.question_line_idx].replace(/\{.*?\}/g, (match) =>
                  "_".repeat(match.length)
                ),
              text1:
                wordList[problem.answer_word_idx].example_translation.split(
                  "\n"
                )[problem.question_line_idx],
              question:
                "주어진 한국어 뜻을 참고하여 예문의 빈칸에 들어갈 가장 알맞은 영단어를 적으시오.",
              word: wordList[problem.answer_word_idx].word,
            });
          });
          testDataList.push({
            type: "exam",
            type_name: typeName,
            data: newExamData,
          });
        });
        newParagraphList.push({
          title: "[단어 테스트]",
          data: testDataList,
        });

        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: newParagraphList,
        };
      } else if (type === "sentence") {
        const title = response.title;
        // For sentence type, keep the original structure with documents and sentence_list
        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: response.data, // Keep the full data object with documents and sentence_list
        };
      } else if (type === "grammar") {
        const title = response.title;
        // For grammar type, keep the original structure with documents
        return {
          id: id,
          title: title,
          type: type,
          author_name: author_name,
          data: response.data, // Keep the full data object with documents
        };
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Unknown error");
    }
  }
);

export const createAssignment = createAsyncThunk(
  "worksheet/createAssignment",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await createAssignmentAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Unknown error");
    }
  }
);

const worksheetSlice = createSlice({
  name: "worksheet",
  initialState,
  reducers: {
    clearWorksheet(state) {
      state.worksheet = { title: "", data: [] }; // 선택된 Worksheet 데이터 초기화
      state.error = null; // 에러 초기화
    },
    setWorksheetTitle(state, action) {
      state.worksheet.title = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(retrieveWorksheet.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.worksheet = { title: "", data: [] }; // 로딩 시작 시 기존 데이터 초기화
      })
      .addCase(retrieveWorksheet.fulfilled, (state, action) => {
        state.loading = false;
        state.worksheet = action.payload; // API로 받은 Worksheet 데이터 저장
      })
      .addCase(retrieveWorksheet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve worksheet";
      })
      .addCase(createAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create assignment";
      });
  },
});

export const { clearWorksheet, setWorksheetTitle } = worksheetSlice.actions;
export default worksheetSlice.reducer;
